import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { toast } from 'sonner';
import { FileText, Printer, Loader2, Activity, TrendingUp, TrendingDown, Minus, Target, Calendar, Users, Sparkles, Copy, Check, ArrowLeft, BarChart3 } from 'lucide-react';

// Color palette for charts
const CHART_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

export default function ReportsPage() {
  const { selectedSchoolId } = useAuth();
  const [searchParams] = useSearchParams();
  const preSelectedStudent = searchParams.get('student');
  
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(preSelectedStudent || '');
  const [reportType, setReportType] = useState('summary');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const reportRef = useRef(null);

  // AI Summary state
  const [aiSummary, setAiSummary] = useState('');
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryCopied, setAiSummaryCopied] = useState(false);

  useEffect(() => {
    fetchStudents();
    // Clear selected student when school changes
    setSelectedStudent('');
    setReport(null);
  }, [selectedSchoolId]);

  useEffect(() => {
    if (preSelectedStudent) {
      setSelectedStudent(preSelectedStudent);
      generateReport(preSelectedStudent);
    }
  }, [preSelectedStudent]);

  const fetchStudents = async () => {
    try {
      const params = { active: true };
      if (selectedSchoolId) {
        params.school_id = selectedSchoolId;
      }
      const response = await apiService.getStudents(params);
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const generateReport = async (studentId = selectedStudent) => {
    if (!studentId) {
      toast.error('Please select a student');
      return;
    }

    setGeneratingReport(true);
    try {
      const response = await apiService.getStudentSummaryReport(studentId);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // AI Summary generation
  const handleGenerateAISummary = async () => {
    if (!selectedStudent) {
      toast.error('Please select a student first');
      return;
    }
    setAiSummaryLoading(true);
    setAiSummary('');
    try {
      const response = await apiService.generateAISummary(selectedStudent);
      if (response.success) {
        setAiSummary(response.summary);
        toast.success('AI summary drafted successfully');
      } else {
        toast.error('Failed to generate AI summary');
      }
    } catch (error) {
      console.error('AI Summary error:', error);
      toast.error('Failed to generate AI summary. Please try again.');
    } finally {
      setAiSummaryLoading(false);
    }
  };

  const handleCopyAISummary = async () => {
    try {
      await navigator.clipboard.writeText(aiSummary);
      setAiSummaryCopied(true);
      toast.success('Summary copied to clipboard');
      setTimeout(() => setAiSummaryCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  // Clear AI summary when student changes
  useEffect(() => {
    setAiSummary('');
  }, [selectedStudent]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTrendIcon = (trend) => {
    if (trend === 'Improving') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'Declining') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-amber-600" />;
  };

  const getReportTitle = () => {
    switch (reportType) {
      case 'summary': return 'Student Summary Report';
      case 'intervention': return 'Intervention Summary Report';
      case 'progress': return 'Progress Summary Report';
      default: return 'Student Report';
    }
  };

  // Render Student Summary Report
  const renderStudentSummary = () => {
    // Get interventions by status
    const activeInterventions = report.interventions.filter(i => i.active_status === 'Active');
    const completedInterventions = report.interventions.filter(i => i.active_status === 'Completed');
    const interventionFocus = [...new Set(activeInterventions.map(i => i.target_skill))].join(', ');

    return (
      <>
        {/* Student Information */}
        <div className="report-section">
          <h3 className="report-section-title">Student Information</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-[#595959]">Student Name</div>
              <div className="font-medium">{report.student.full_name}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Grade</div>
              <div className="font-medium">{report.student.grade}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Teacher</div>
              <div className="font-medium">{report.student.teacher_name}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">School</div>
              <div className="font-medium">{report.student.school_name}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Current Tier</div>
              <div className="font-medium">{report.student.current_tier}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Intervention Focus</div>
              <div className="font-medium">{interventionFocus || '—'}</div>
            </div>
          </div>
        </div>

      {/* Support Timeline */}
      <div className="report-section">
        <h3 className="report-section-title">Support Timeline</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-[#595959]">Support Start Date</div>
            <div className="font-medium">{formatDate(report.student.support_start_date)}</div>
          </div>
          <div>
            <div className="text-sm text-[#595959]">Review Date</div>
            <div className="font-medium">{formatDate(report.student.review_date)}</div>
          </div>
          <div>
            <div className="text-sm text-[#595959]">Current Status</div>
            <div className="font-medium">{report.student.status}</div>
          </div>
          <div>
            <div className="text-sm text-[#595959]">Current Trend</div>
            <div className={`font-medium flex items-center gap-1 ${
              report.trend === 'Improving' ? 'text-green-600' :
              report.trend === 'Declining' ? 'text-red-600' :
              'text-amber-600'
            }`}>
              {getTrendIcon(report.trend)}
              {report.trend}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Snapshot */}
      <div className="report-section">
        <h3 className="report-section-title">Progress Snapshot</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-[#F3F4F1] rounded-lg p-4 text-center">
            <div className="text-sm text-[#595959] mb-1">Baseline</div>
            <div className="text-2xl font-bold">{report.student.baseline_score || '—'}</div>
            <div className="text-xs text-[#A3A3A3]">{report.student.baseline_measure_name}</div>
          </div>
          <div className="bg-indigo-50 rounded-lg p-4 text-center">
            <div className="text-sm text-[#595959] mb-1">Latest Score</div>
            <div className="text-2xl font-bold text-[#2C4C3B]">{report.latest_score || '—'}</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 text-center">
            <div className="text-sm text-[#595959] mb-1">Goal</div>
            <div className="text-2xl font-bold text-emerald-600">{report.student.goal_score || '—'}</div>
          </div>
        </div>
      </div>

      {/* Brief Intervention List - Separated by Status */}
      <div className="report-section">
        <h3 className="report-section-title">Active Interventions ({activeInterventions.length})</h3>
        {activeInterventions.length > 0 ? (
          <div className="space-y-2">
            {activeInterventions.map((intervention) => (
              <div key={intervention.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                <div>
                  <div className="font-medium">{intervention.intervention_name}</div>
                  <div className="text-sm text-[#595959]">{intervention.target_skill} • {intervention.provider_name}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#595959]">{intervention.frequency_per_week}x/week</span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">Active</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#595959] text-sm">No active interventions</p>
        )}
      </div>

      {/* Completed Interventions */}
      {completedInterventions.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">Completed Interventions ({completedInterventions.length})</h3>
          <div className="space-y-2">
            {completedInterventions.map((intervention) => (
              <div key={intervention.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div>
                  <div className="font-medium">{intervention.intervention_name}</div>
                  <div className="text-sm text-[#595959]">{intervention.target_skill} • {intervention.provider_name}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#595959]">{intervention.start_date} — {intervention.end_date || 'N/A'}</span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">Completed</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Brief Recent Progress */}
      <div className="report-section">
        <h3 className="report-section-title">Recent Progress ({report.recent_progress.length} data points)</h3>
        {report.recent_progress.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Score</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {report.recent_progress.slice(0, 5).map((p) => (
                  <tr key={p.id}>
                    <td>{p.date_collected}</td>
                    <td className="font-semibold">{p.score}</td>
                    <td>{p.score_unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {report.recent_progress.length > 5 && (
              <p className="text-sm text-[#595959] mt-2">+ {report.recent_progress.length - 5} more entries</p>
            )}
          </div>
        ) : (
          <p className="text-[#595959] text-sm">No progress data available</p>
        )}
      </div>

      {/* Summary Narrative */}
      <div className="report-section">
        <h3 className="report-section-title">Summary</h3>
        <div className="report-narrative" data-testid="report-narrative">
          {report.narrative}
        </div>
      </div>
    </>
    );
  };

  // Render Intervention Summary Report
  const renderInterventionSummary = () => {
    const activeInterventions = report.interventions.filter(i => i.active_status === 'Active');
    const completedInterventions = report.interventions.filter(i => i.active_status === 'Completed');
    const discontinuedInterventions = report.interventions.filter(i => i.active_status === 'Discontinued');
    const allInterventions = report.all_interventions || report.interventions;

    return (
      <>
        {/* Student Header */}
        <div className="report-section">
          <h3 className="report-section-title">Student</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-[#595959]">Name</div>
              <div className="font-medium">{report.student.full_name}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Grade</div>
              <div className="font-medium">{report.student.grade}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Current Tier</div>
              <div className="font-medium">{report.student.current_tier}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Support Start</div>
              <div className="font-medium">{formatDate(report.student.support_start_date)}</div>
            </div>
          </div>
        </div>

        {/* Intervention Overview */}
        <div className="report-section">
          <h3 className="report-section-title">Intervention Overview</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{activeInterventions.length}</div>
              <div className="text-sm text-[#595959]">Active</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{completedInterventions.length}</div>
              <div className="text-sm text-[#595959]">Completed</div>
            </div>
            <div className="bg-[#F3F4F1] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#595959]">{discontinuedInterventions.length}</div>
              <div className="text-sm text-[#595959]">Discontinued</div>
            </div>
          </div>
        </div>

        {/* Active Interventions Detail */}
        {activeInterventions.length > 0 && (
          <div className="report-section">
            <h3 className="report-section-title">Active Interventions</h3>
            <div className="space-y-4">
              {activeInterventions.map((intervention) => (
                <div key={intervention.id} className="border border-green-200 rounded-lg p-4 bg-green-50/50">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-lg">{intervention.intervention_name}</h4>
                      <div className="text-sm text-[#595959]">{intervention.target_skill}</div>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">Active</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-[#595959]">Provider</div>
                      <div className="font-medium">{intervention.provider_name}</div>
                    </div>
                    <div>
                      <div className="text-[#595959]">Frequency</div>
                      <div className="font-medium">{intervention.frequency_per_week}x per week</div>
                    </div>
                    <div>
                      <div className="text-[#595959]">Session Length</div>
                      <div className="font-medium">{intervention.session_minutes} minutes</div>
                    </div>
                    <div>
                      <div className="text-[#595959]">Group Size</div>
                      <div className="font-medium">{intervention.group_size || 1}:1</div>
                    </div>
                    <div>
                      <div className="text-[#595959]">Start Date</div>
                      <div className="font-medium">{intervention.start_date}</div>
                    </div>
                    <div>
                      <div className="text-[#595959]">End Date</div>
                      <div className="font-medium">{intervention.end_date || 'Ongoing'}</div>
                    </div>
                    <div>
                      <div className="text-[#595959]">Baseline</div>
                      <div className="font-medium">{intervention.baseline_score ?? '—'}</div>
                    </div>
                    <div>
                      <div className="text-[#595959]">Goal</div>
                      <div className="font-medium">{intervention.goal_score ?? '—'}</div>
                    </div>
                  </div>
                  {intervention.fidelity_notes && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <div className="text-sm text-[#595959]">Fidelity Notes</div>
                      <div className="text-sm">{intervention.fidelity_notes}</div>
                    </div>
                  )}
                  {intervention.intervention_notes && (
                    <div className="mt-2">
                      <div className="text-sm text-[#595959]">Notes</div>
                      <div className="text-sm">{intervention.intervention_notes}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed/Discontinued Interventions */}
        {(completedInterventions.length > 0 || discontinuedInterventions.length > 0) && (
          <div className="report-section">
            <h3 className="report-section-title">Past Interventions</h3>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Intervention</th>
                    <th>Target Skill</th>
                    <th>Provider</th>
                    <th>Duration</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...completedInterventions, ...discontinuedInterventions].map((intervention) => (
                    <tr key={intervention.id}>
                      <td className="font-medium">{intervention.intervention_name}</td>
                      <td>{intervention.target_skill}</td>
                      <td>{intervention.provider_name}</td>
                      <td>{intervention.start_date} — {intervention.end_date || 'N/A'}</td>
                      <td>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          intervention.active_status === 'Completed' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-[#F3F4F1] text-[#1C1C1C]'
                        }`}>
                          {intervention.active_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Intervention Summary */}
        <div className="report-section">
          <h3 className="report-section-title">Summary</h3>
          <div className="report-narrative">
            {report.student.full_name} currently has {activeInterventions.length} active intervention{activeInterventions.length !== 1 ? 's' : ''} 
            {activeInterventions.length > 0 && (
              <> targeting: {[...new Set(activeInterventions.map(i => i.target_skill))].join(', ')}</>
            )}.
            {completedInterventions.length > 0 && (
              <> {completedInterventions.length} intervention{completedInterventions.length !== 1 ? 's have' : ' has'} been completed.</>
            )}
            {report.student.review_date && (
              <> Next review is scheduled for {formatDate(report.student.review_date)}.</>
            )}
          </div>
        </div>
      </>
    );
  };

  // Render Progress Summary Report
  const renderProgressSummary = () => {
    // Group progress by intervention
    const progressByIntervention = {};
    report.recent_progress.forEach(p => {
      const key = p.intervention_id || 'unlinked';
      if (!progressByIntervention[key]) {
        progressByIntervention[key] = {
          intervention: report.interventions.find(i => i.id === p.intervention_id),
          entries: []
        };
      }
      progressByIntervention[key].entries.push(p);
    });

    return (
      <>
        {/* Student Header */}
        <div className="report-section">
          <h3 className="report-section-title">Student</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-[#595959]">Name</div>
              <div className="font-medium">{report.student.full_name}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Grade</div>
              <div className="font-medium">{report.student.grade}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Current Tier</div>
              <div className="font-medium">{report.student.current_tier}</div>
            </div>
            <div>
              <div className="text-sm text-[#595959]">Current Trend</div>
              <div className={`font-medium flex items-center gap-1 ${
                report.trend === 'Improving' ? 'text-green-600' :
                report.trend === 'Declining' ? 'text-red-600' :
                'text-amber-600'
              }`}>
                {getTrendIcon(report.trend)}
                {report.trend}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="report-section">
          <h3 className="report-section-title">Progress Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#F3F4F1] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold">{report.recent_progress.length}</div>
              <div className="text-sm text-[#595959]">Total Data Points</div>
            </div>
            <div className="bg-[#F3F4F1] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold">{Object.keys(progressByIntervention).filter(k => k !== 'unlinked').length}</div>
              <div className="text-sm text-[#595959]">Interventions Tracked</div>
            </div>
            <div className="bg-indigo-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#2C4C3B]">{report.latest_score || '—'}</div>
              <div className="text-sm text-[#595959]">Latest Score</div>
            </div>
            <div className="bg-[#F3F4F1] rounded-lg p-4 text-center">
              <div className="text-sm font-medium">{report.recent_progress[0]?.date_collected || '—'}</div>
              <div className="text-sm text-[#595959]">Last Progress Date</div>
            </div>
          </div>
        </div>

        {/* Progress by Intervention */}
        {Object.entries(progressByIntervention).map(([key, data]) => {
          if (key === 'unlinked' && data.entries.length === 0) return null;
          const intervention = data.intervention;
          const entries = data.entries.sort((a, b) => new Date(b.date_collected) - new Date(a.date_collected));
          
          return (
            <div key={key} className="report-section">
              <h3 className="report-section-title">
                {intervention ? (
                  <span className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    {intervention.intervention_name}
                    <span className="text-sm font-normal text-[#595959]">({intervention.target_skill})</span>
                  </span>
                ) : (
                  'Progress Data (Not Linked to Intervention)'
                )}
              </h3>
              
              {intervention && (
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <div className="text-sm text-[#595959]">Baseline</div>
                    <div className="text-xl font-bold">{intervention.baseline_score ?? '—'}</div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <div className="text-sm text-[#595959]">Latest</div>
                    <div className="text-xl font-bold text-[#2C4C3B]">{entries[0]?.score ?? '—'}</div>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <div className="text-sm text-[#595959]">Goal</div>
                    <div className="text-xl font-bold text-emerald-600">{intervention.goal_score ?? '—'}</div>
                  </div>
                </div>
              )}

              {/* Progress Chart for this intervention */}
              {entries.length >= 2 && (
                <div className="mb-4 p-4 bg-white border border-[#E5E5E5] rounded-lg report-chart-container">
                  <h4 className="text-sm font-medium text-[#1C1C1C] mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Progress Chart
                  </h4>
                  
                  {/* Screen version - uses ResponsiveContainer */}
                  <div className="screen-only">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart 
                        data={[...entries].reverse().map(e => ({
                          date: e.date_collected,
                          score: e.score
                        }))}
                        margin={{ top: 10, right: 50, left: 10, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Line 
                          type="monotone" 
                          dataKey="score" 
                          stroke="#4f46e5" 
                          strokeWidth={2}
                          dot={{ fill: '#4f46e5', r: 5 }}
                        />
                        {intervention?.goal_score && (
                          <ReferenceLine 
                            y={intervention.goal_score} 
                            stroke="#10b981" 
                            strokeDasharray="5 5"
                            label={{ value: 'Goal', fill: '#10b981', fontSize: 10 }}
                          />
                        )}
                        {intervention?.baseline_score && (
                          <ReferenceLine 
                            y={intervention.baseline_score} 
                            stroke="#f59e0b" 
                            strokeDasharray="5 5"
                            label={{ value: 'Baseline', fill: '#f59e0b', fontSize: 10 }}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Print version - uses fixed dimensions */}
                  <div className="print-only-chart">
                    <LineChart 
                      width={600}
                      height={260}
                      data={[...entries].reverse().map(e => ({
                        date: e.date_collected,
                        score: e.score
                      }))}
                      margin={{ top: 15, right: 45, left: 15, bottom: 15 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 9 }} />
                      <Line 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#4f46e5" 
                        strokeWidth={2}
                        dot={{ fill: '#4f46e5', r: 4 }}
                      />
                      {intervention?.goal_score && (
                        <ReferenceLine 
                          y={intervention.goal_score} 
                          stroke="#10b981" 
                          strokeDasharray="5 5"
                          label={{ value: 'Goal', fill: '#10b981', fontSize: 9 }}
                        />
                      )}
                      {intervention?.baseline_score && (
                        <ReferenceLine 
                          y={intervention.baseline_score} 
                          stroke="#f59e0b" 
                          strokeDasharray="5 5"
                          label={{ value: 'Baseline', fill: '#f59e0b', fontSize: 9 }}
                        />
                      )}
                    </LineChart>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Measure</th>
                      <th>Score</th>
                      <th>Unit</th>
                      <th>Note</th>
                      <th>Entered By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((p) => (
                      <tr key={p.id}>
                        <td>{p.date_collected}</td>
                        <td>{p.measure_name}</td>
                        <td className="font-semibold">{p.score}</td>
                        <td>{p.score_unit}</td>
                        <td className="text-sm text-[#595959]">{p.note || '—'}</td>
                        <td>
                          {p.entered_by_name ? (
                            <div>
                              <span className="text-sm">{p.entered_by_name}</span>
                              {p.entered_by_email && (
                                <span className="block text-xs text-[#A3A3A3]">{p.entered_by_email}</span>
                              )}
                            </div>
                          ) : p.entered_by ? (
                            <span className="text-sm">{p.entered_by}</span>
                          ) : (
                            <span className="text-sm text-[#A3A3A3]">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-sm text-[#595959] mt-2">{entries.length} data point{entries.length !== 1 ? 's' : ''}</div>
            </div>
          );
        })}

        {/* Progress Summary */}
        <div className="report-section">
          <h3 className="report-section-title">Summary</h3>
          <div className="report-narrative">
            {report.student.full_name} has {report.recent_progress.length} progress data point{report.recent_progress.length !== 1 ? 's' : ''} on record.
            {report.latest_score && (
              <> The most recent score is {report.latest_score}.</>
            )}
            {report.trend && (
              <> Overall trend is <strong className={
                report.trend === 'Improving' ? 'text-green-600' :
                report.trend === 'Declining' ? 'text-red-600' :
                'text-amber-600'
              }>{report.trend.toLowerCase()}</strong>.</>
            )}
            {report.student.goal_score && (
              <> Target goal is {report.student.goal_score}.</>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="page-container reports-page" data-testid="reports-page">
      {/* Back Navigation - Hidden in print */}
      <div className="no-print">
        {selectedStudent ? (
          <Link 
            to={`/students/${selectedStudent}`}
            className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#2C4C3B] mb-4 transition-colors"
            data-testid="back-to-student-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Student Profile
          </Link>
        ) : (
          <Link 
            to="/students"
            className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#2C4C3B] mb-4 transition-colors"
            data-testid="back-to-students-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Students
          </Link>
        )}
      </div>

      {/* Page header - Hidden in print */}
      <div className="page-header no-print">
        <h1 className="page-title">Reports</h1>
        <p className="page-subtitle">Generate and print student progress reports</p>
      </div>

      {/* Report Selection - Hidden in print */}
      <div className="card mb-6 no-print" data-testid="report-controls">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="form-label">Select Student</label>
              <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                <SelectTrigger data-testid="student-select">
                  <SelectValue placeholder="Choose a student" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.full_name} - Grade {student.grade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[180px]">
              <label className="form-label">Report Type</label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Student Summary</SelectItem>
                  <SelectItem value="intervention">Intervention Summary</SelectItem>
                  <SelectItem value="progress">Progress Summary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => generateReport()}
              disabled={generatingReport || !selectedStudent}
              className="bg-[#2C4C3B] hover:bg-[#1E3629]"
              data-testid="generate-report-btn"
            >
              {generatingReport ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>

            {report && (
              <Button variant="outline" onClick={handlePrint} data-testid="print-report-btn">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Report Display */}
      {report && (
        <div className="report-container" ref={reportRef} data-testid="report-content">
          <div className="report-header">
            <h2 className="report-title">MTSS {getReportTitle()}</h2>
            <p className="report-subtitle">Generated: {formatDate(report.generated_at)}</p>
          </div>

          {/* Render different content based on report type */}
          {reportType === 'summary' && renderStudentSummary()}
          {reportType === 'intervention' && renderInterventionSummary()}
          {reportType === 'progress' && renderProgressSummary()}

          {/* Notes (shown on all report types if present) */}
          {report.student.notes && (
            <div className="report-section">
              <h3 className="report-section-title">Additional Notes</h3>
              <p className="text-sm text-[#595959]">{report.student.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* AI Summary Card - Separate optional feature */}
      {report && (
        <div className="card mt-6 print:hidden" data-testid="ai-summary-card">
          <div className="card-header flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              <h3 className="card-title">AI Summary (Optional)</h3>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleGenerateAISummary}
              disabled={aiSummaryLoading}
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
              data-testid="generate-ai-summary-btn"
            >
              {aiSummaryLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate AI Summary
                </>
              )}
            </Button>
          </div>
          <div className="card-content">
            {!aiSummary && !aiSummaryLoading && (
              <div className="p-6 bg-[#F3F4F1] rounded-lg text-center">
                <Sparkles className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-[#595959]">Generate an AI-drafted narrative summary</p>
                <p className="text-xs text-[#A3A3A3] mt-1">Uses student data, interventions, and progress to create a professional summary</p>
              </div>
            )}
            
            {aiSummaryLoading && (
              <div className="p-6 bg-purple-50 rounded-lg text-center">
                <Loader2 className="h-8 w-8 text-purple-400 mx-auto mb-2 animate-spin" />
                <p className="text-sm text-purple-600">Generating AI summary...</p>
                <p className="text-xs text-purple-400 mt-1">This may take a few seconds</p>
              </div>
            )}
            
            {aiSummary && !aiSummaryLoading && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#595959]">AI-generated draft • Review and edit as needed</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCopyAISummary}
                    className="text-[#595959] hover:text-[#1C1C1C]"
                  >
                    {aiSummaryCopied ? (
                      <>
                        <Check className="h-4 w-4 mr-1 text-green-500" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <textarea
                  value={aiSummary}
                  onChange={(e) => setAiSummary(e.target.value)}
                  className="w-full min-h-[200px] p-4 border border-[#E5E5E5] rounded-lg text-sm text-[#1C1C1C] leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
                  placeholder="AI summary will appear here..."
                  data-testid="ai-summary-textarea"
                />
                <p className="text-xs text-[#A3A3A3]">
                  This is a draft for your review. Edit as needed, then copy to use elsewhere. Changes here are not saved automatically.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {!report && !generatingReport && (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon">
              <FileText size={32} />
            </div>
            <h3 className="empty-title">No report generated</h3>
            <p className="empty-text">Select a student and click "Generate Report" to view their summary</p>
          </div>
        </div>
      )}
    </div>
  );
}
